import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useRovoAgentCreateAnalytics } from '@atlaskit/rovo-agent-analytics/create';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';

import { Ati } from '@atlassian/ari';
import { getAtlassianStudioNLCreateUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { ItemTitleWithLozenge } from '../ItemTitle.tsx';

import type {
	useAgentItemStudioPermissionsQuery$data,
	useAgentItemStudioPermissionsQuery$variables,
} from './__generated__/useAgentItemStudioPermissionsQuery.graphql.ts';
import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

const CONNIE_CREATE_SOURCE = 'connie-create';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.agent.title',
		defaultMessage: 'Create agent',
		description: 'Title indicating that this option will redirect to the Studio Create Agent page',
	},
	newLozenge: {
		id: 'create-blank-fabric-page.universal-create.agent.new-lozenge',
		defaultMessage: 'New',
		description: 'Text for the new lozenge indicating this is a new feature',
	},
});

const AgentMenuItem = ({ source }: Partial<Pick<UseMenuItemProps, 'source'>>) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'createAgentPopoverItem',
				attributes: {
					source,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	return (
		<ItemTitleWithLozenge lozenge={formatMessage(messages.newLozenge)}>
			{formatMessage(messages.title)}
		</ItemTitleWithLozenge>
	);
};

export function useAgentItem({
	onClick,
	source,
	isMenuOpen,
}: UseMenuItemProps & { isMenuOpen?: boolean }): [
	item: UseMenuItemReturnProps,
	isEligible: boolean,
] {
	const { cloudId, isRovoLLMEnabled, accessStatus, isLoggedIn } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [csid, { trackCreateSessionStart }] = useRovoAgentCreateAnalytics({
		touchPoint: CONNIE_CREATE_SOURCE,
	});

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const shouldCheckAgentsPermissions =
		isRovoLLMEnabled &&
		!isAnonymousUser &&
		!!cloudId &&
		!!isMenuOpen &&
		fg('cc_fd_agent_creation_connie_create_gate');

	const { data } = useQueryInterop<
		useAgentItemStudioPermissionsQuery$data,
		useAgentItemStudioPermissionsQuery$variables
	>(
		graphql`
			query useAgentItemStudioPermissionsQuery($cloudId: ID!) {
				atlassianStudio_userSiteContext(cloudId: $cloudId) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							isAbleToCreateAgents
						}
					}
				}
			}
		`,
		{
			variables: { cloudId },
			skip: !shouldCheckAgentsPermissions,
			interopApolloAggClient: true,
			onCompleted: (resp) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source,
						action: 'success',
						actionSubject: 'studioPermissionsQuery',
						actionSubjectId: 'confluenceCreateMenu',
						attributes: {
							isAbleToCreateAgents:
								resp?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents,
						},
					},
				}).fire();
			},
			onError: (error) => {
				if (
					!isErrorMarkedAsHandled(error) &&
					(isUnauthorizedError(error) || error?.message.includes('Failed to fetch'))
				) {
					markErrorAsHandled(error);
					return;
				}

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source,
						action: 'error',
						actionSubject: 'studioPermissionsQuery',
						actionSubjectId: 'confluenceCreateMenu',
						attributes: {
							errorMessage: error.message,
						},
					},
				}).fire();
			},
		},
	);

	const hasPermission =
		data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents ?? false;
	const isEligible = shouldCheckAgentsPermissions && hasPermission;
	const createAgentUrl = getAtlassianStudioNLCreateUrl(cloudId, CONNIE_CREATE_SOURCE, csid);

	const handleClick = useCallback(() => {
		trackCreateSessionStart();
		onClick('createAgentPopoverItem');
	}, [onClick, trackCreateSessionStart]);

	return [
		{
			ati: Ati.parse('ati:cloud:confluence:rovo'),
			children: <AgentMenuItem source={source} />,
			href: createAgentUrl,
			target: '_blank',
			onClick: handleClick,
			elemBefore: AiAgentIcon,
			testId: 'create-menu-link-item',
		},
		isEligible,
	];
}
